import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';

import FilePreviewer from '@components/FilePreviewer';
import LoaderComponent from '@components/LoaderComponent';
import NoData from '@components/NoData';
import { toastError, toastSuccess } from '@components/Toast';
import TopGallerySlider from '@components/TopGallerySlider';

import BlPaymentInfo from '@features/order/components/payment/BlPaymentInfo';

import ApiClient from '@lib/apiClient';

import { getShortName, onUploadFile, validateNumber } from '@utils/Util';

const Payment = ({ orderId }) => {
	const [billOfLandingList, setBillOfLandingList] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState({});
	useEffect(() => {
		getBillOfLandingList(orderId, true);
	}, [orderId]);

	const getBillOfLandingList = async (orderId, isInitialRender = false) => {
		setIsLoading(true);
		await ApiClient.GET('billOfLandingAction', orderId)
			.then(({ data }) => {
				setIsLoading(false);
				setBillOfLandingList(data);
				if (isInitialRender) {
					getPaymentDetailsInfo(data[0].id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const getPaymentDetailsInfo = async (billId) => {
		setIsLoading(true);
		await ApiClient.GET('billOfLandingAction', `${billId}/details`)
			.then(({ data }) => {
				setIsLoading(false);
				setPaymentDetails(data);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onUploadBlDoc = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('billOfLandingAction', [data], `${paymentDetails?.id}/document`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.message) {
					getPaymentDetailsInfo(paymentDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onDeleteBlDoc = async (id) => {
		setIsLoading(true);
		await ApiClient.DELETE('billOfLandingAction', {}, `${paymentDetails?.id}/document/${id}`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					let availableItem = paymentDetails?.documentResponseList?.filter(
						(item) => item.id !== id
					);
					setPaymentDetails({ ...paymentDetails, documentResponseList: availableItem });
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onUploadPaymentDoc = async (data) => {
		setIsLoading(true);
		await ApiClient.POST(
			'billOfLandingAction',
			[data],
			`${paymentDetails?.id}/payment/${data.id}/document`
		)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.message) {
					getPaymentDetailsInfo(paymentDetails?.id);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onDeletePaymentDoc = async (type, paymentId, documentId) => {
		setIsLoading(true);
		await ApiClient.DELETE(
			'billOfLandingAction',
			{},
			`${paymentDetails?.id}/payment/${paymentId}/document/${documentId}`
		)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					if (type === 'BUYER') {
						let availableItem =
							paymentDetails?.buyerBLPaymentResponse?.documentResponseList?.filter(
								(item) => item.id !== documentId
							);
						setPaymentDetails({
							...paymentDetails,
							buyerBLPaymentResponse: {
								...paymentDetails?.buyerBLPaymentResponse,
								documentResponseList: availableItem
							}
						});
					} else {
						let availableItem =
							paymentDetails?.factoryBLPaymentResponse?.documentResponseList?.filter(
								(item) => item.id !== documentId
							);
						setPaymentDetails({
							...paymentDetails,
							factoryBLPaymentResponse: {
								...paymentDetails?.factoryBLPaymentResponse,
								documentResponseList: availableItem
							}
						});
					}
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onBillCardClick = (item) => {
		if (paymentDetails.id !== item.id) {
			getPaymentDetailsInfo(item.id);
		}
	};

	const onUpdate = async () => {
		let body = {
			blDate: paymentDetails?.blDate,
			blNo: paymentDetails?.blNo || '',
			buyerPaymentTerms: paymentDetails?.buyerPaymentTerms || null,
			factoryPaymentTerms: paymentDetails?.factoryPaymentTerms || null,
			id: paymentDetails?.id
		};
		setIsLoading(true);
		await ApiClient.POST('billOfLandingAction', body, `${paymentDetails?.id}/update`)
			.then(({ data }) => {
				setIsLoading(false);
				setPaymentDetails(data);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdatePaymentInfo = async (type) => {
		let itemDetails =
			type === 'BUYER'
				? paymentDetails?.buyerBLPaymentResponse
				: paymentDetails?.factoryBLPaymentResponse;
		let body = {
			actualPayment: itemDetails?.actualPayment || null,
			actualPaymentDate: itemDetails?.actualPaymentDate || null,
			claim: itemDetails?.claim || null,
			id: itemDetails?.id,
			remarks: itemDetails?.remarks
		};
		setIsLoading(true);
		await ApiClient.POST('billOfLandingAction', body, `${paymentDetails?.id}/payment-update`)
			.then(({ data }) => {
				setIsLoading(false);
				if (type === 'BUYER') {
					setPaymentDetails({ ...paymentDetails, buyerBLPaymentResponse: data });
				} else {
					setPaymentDetails({ ...paymentDetails, factoryBLPaymentResponse: data });
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onChangeBlInfo = (e) => {
		let { name, value } = e.target;
		setPaymentDetails({
			...paymentDetails,
			[name]: value
		});
	};

	const onChangePaymentInfo = (e, type) => {
		let { name, value } = e.target;
		let info = { ...paymentDetails[type] };
		info[name] = value;
		setPaymentDetails({
			...paymentDetails,
			[type]: info
		});
	};

	const onComplete = async (id) => {
		setIsLoading(true);
		await ApiClient.POST('billOfLandingAction', {}, `${id}/complete`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					toastSuccess('Bill of landing completed successfully');
					let supplierList = billOfLandingList?.map((item) => {
						if (item.id === data.id) {
							item.completedStatus = 'COMPLETED';
						}
						return item;
					});
					setBillOfLandingList([...supplierList]);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="create-order-container">
				{billOfLandingList.length === 0 && !isLoading ? (
					<NoData message={'Please update commercial invoice'} />
				) : (
					<>
						<TopGallerySlider
							itemList={billOfLandingList}
							details={paymentDetails}
							onCardClick={onBillCardClick}
							type={'PAYMENT'}
							onComplete={onComplete}
						/>
						<div className="payment-info-container">
							<div className="title">
								<h2 className="medium-16">Payment info</h2>
							</div>
							<div className="bl-tracking-section">
								<div className="one-third">
									<div className="invoices-details">
										<p className="mb-5">BL TRACKING</p>
										<div className="single-row">
											<p className="regular-12 gray_dark_02">PO</p>
											{paymentDetails?.poNumberList?.map((item, idx) => (
												<p
													className="regular-12 gray_dark_01"
													key={`po_number_${idx}`}
												>
													{item}
												</p>
											))}
										</div>
										<div className="single-row">
											<p className="regular-12 gray_dark_02">COMMERCIAL IN</p>
											<p className="regular-12 gray_dark_01">
												{paymentDetails?.commercialInvoiceNo}
											</p>
										</div>
										<div className="single-row">
											<p className="regular-12 gray_dark_02">SALES CONTRACT</p>
											<p className="regular-12 gray_dark_01">
												{paymentDetails?.salesContractNo}
											</p>
										</div>
									</div>
								</div>
								<div className="one-third">
									<div className="bl-form-container container">
										<div className="row">
											<div className="col-md-12">
												<div className="form-group">
													<label htmlFor="blNo">BL number</label>
													<input
														type="text"
														autoComplete="off"
														id="blNo"
														name="blNo"
														value={paymentDetails?.blNo ?? ''}
														onChange={(e) => onChangeBlInfo(e)}
														onBlur={onUpdate}
													/>
												</div>
											</div>
											<div className="col-md-12">
												<div className="form-group">
													<label htmlFor="bldate">BL date</label>
													<input
														type="date"
														id="blDate"
														name="blDate"
														value={paymentDetails?.blDate ?? ''}
														onChange={(e) => onChangeBlInfo(e)}
														onBlur={onUpdate}
													/>
												</div>
											</div>
											<div className="col-sm-12 col-md-6 pr-2">
												<div className="form-group">
													<label htmlFor="p-date">
														Buyer payment terms (In days)
													</label>
													<input
														type="number"
														id="buyerPaymentTerms"
														name="buyerPaymentTerms"
														value={paymentDetails?.buyerPaymentTerms ?? ''}
														onChange={(e) => onChangeBlInfo(e)}
														onBlur={onUpdate}
														onKeyPress={validateNumber}
													/>
												</div>
											</div>
											<div className="col-sm-12 col-md-6 pl-2">
												<div className="form-group">
													<label htmlFor="per-po-date">
														Buyer payment date(As per PO)
													</label>
													<input
														type="date"
														id="buyerPaymentTermsDate"
														name="buyerPaymentTermsDate"
														disabled
														value={paymentDetails?.buyerPaymentDate ?? ''}
													/>
												</div>
											</div>
											<div className="col-sm-12 col-md-6 pr-2">
												<div className="form-group">
													<label htmlFor="f-pay-date">
														Factory payment terms (in days)
													</label>
													<input
														type="number"
														id="factoryPaymentTerms"
														name="factoryPaymentTerms"
														value={paymentDetails?.factoryPaymentTerms ?? ''}
														onChange={(e) => onChangeBlInfo(e)}
														onBlur={onUpdate}
														onKeyPress={validateNumber}
													/>
												</div>
											</div>
											<div className="col-sm-12 col-md-6 pl-2">
												<div className="form-group">
													<label htmlFor="per-po-fdate">
														Buyer payment date(As per PO)
													</label>
													<input
														type="date"
														id="factoryPaymentTermsDate"
														name="factoryPaymentTermsDate"
														disabled
														value={paymentDetails?.factoryPaymentDate ?? ''}
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="one-third">
									<div className="upload-documents-container bg-white">
										<div className="uploded-documents pt-2">
											{paymentDetails?.documentResponseList?.length > 0 ? (
												paymentDetails?.documentResponseList?.map((item) => (
													<div
														className="single-uploaded-doc d-flex align-items-center"
														key={item?.id}
													>
														<div className="doc-names">
															<FilePreviewer docUrl={item?.docUrl} />
															<Tooltip title={item?.name} placement="top" arrow>
																<a
																	target="_blank"
																	href={item?.docUrl}
																	className="regular-12 primary_bright_01 cursor-pointer"
																	rel="noreferrer"
																>
																	{getShortName(item?.name, 15)}
																</a>
															</Tooltip>
														</div>
														<div className="delete-btn">
															<img
																src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
																alt="delete"
																onClick={() => onDeleteBlDoc(item.id)}
															/>
														</div>
													</div>
												))
											) : (
												<p className="regular-14 gray_dark_02 text-center no-doc">
													No document uploaded
												</p>
											)}
										</div>
									</div>
									<div className="upload-bl text-center">
										<label htmlFor="bl-upload" className="drag-upload">
											<img
												src={`${process.env.PUBLIC_URL}/icons/upload-sm.svg`}
												alt="upload"
											/>{' '}
											<span>Document</span>
										</label>
										<input
											type="file"
											className="file-upload d-none"
											onChange={(e) =>
												onUploadFile(e, 'ACCESSORIES_DESIGN', onUploadBlDoc, 'MULTI')
											}
											name="bl-upload"
											id="bl-upload"
											multiple
											accept="*"
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="buyer-and-factory-payments-details pb-4">
							<BlPaymentInfo
								data={paymentDetails?.buyerBLPaymentResponse}
								onChangePaymentInfo={(e) => onChangePaymentInfo(e, 'buyerBLPaymentResponse')}
								onUpdatePaymentInfo={() => onUpdatePaymentInfo('BUYER')}
								onUploadPaymentDoc={onUploadPaymentDoc}
								onDeletePaymentDoc={(paymentId, documentId) =>
									onDeletePaymentDoc('BUYER', paymentId, documentId)
								}
								type="BUYER"
							/>

							<BlPaymentInfo
								data={paymentDetails?.factoryBLPaymentResponse}
								onChangePaymentInfo={(e) =>
									onChangePaymentInfo(e, 'factoryBLPaymentResponse')
								}
								onUpdatePaymentInfo={() => onUpdatePaymentInfo('FACTORY')}
								onUploadPaymentDoc={onUploadPaymentDoc}
								onDeletePaymentDoc={(paymentId, documentId) =>
									onDeletePaymentDoc('FACTORY', paymentId, documentId)
								}
								type="FACTORY"
							/>
						</div>
					</>
				)}
			</div>
		</LoaderComponent>
	);
};

export default Payment;
